import React from "react";
import { useTranslation } from "react-i18next";
import SoundLangControls from "./SoundLangControls";

/**
 * Shared chrome for games & most kid screens: back pill, centred
 * emoji+title, and a right-hand status slot (score/lives/timer).
 * Centres `children` in a max-w column on the `bg` field.
 *
 * Props: title, emoji, onBack, right, bg, theme ('dark'|'light'), children
 * `showControls`: mount the 🔊/🌐 SoundLangControls next to `right`. Opt-in —
 * leave off on timed/active gameplay screens (quiz, typing, tracing, etc.)
 * where an accidental tap mid-question would be disruptive; turn on for
 * lobby/menu/browse screens (leaderboard, trophy room, watch zone, hub).
 */
export default function GameShell({ title, emoji, onBack, right, bg, theme = "dark", children, maxWidth = 760, showControls = false }) {
  const { t } = useTranslation();
  const onLight = theme === "light";

  return (
    <div
      className="min-h-[100dvh] flex flex-col"
      style={{ background: bg || "linear-gradient(160deg, #FF8A4C 0%, #EE5F2B 55%, #D14E22 100%)" }}
    >
      <header className="flex items-center justify-between gap-3 px-4 py-3 md:px-6 md:py-4">
        <button
          type="button"
          onClick={onBack}
          className="inline-flex items-center gap-1.5 rounded-full px-4 min-h-[44px] font-body font-extrabold text-[13px] shrink-0"
          style={
            onLight
              ? { background: "#fff", color: "#2A2620", boxShadow: "0 3px 0 rgba(0,0,0,.08)" }
              : { background: "rgba(0,0,0,.16)", color: "#fff" }
          }
        >
          <span className="rtl:hidden">←</span>
          <span className="hidden rtl:inline">→</span>
          <span>{t("kids.common.back")}</span>
        </button>

        <h1
          className="flex-1 text-center font-display font-semibold text-[18px] md:text-[22px] truncate px-2"
          style={{ color: onLight ? "#2A2620" : "#fff" }}
        >
          {emoji && <span className="me-1.5">{emoji}</span>}
          {title}
        </h1>

        <div className="shrink-0 min-w-[44px] flex items-center justify-end gap-2">
          {right}
          {showControls && <SoundLangControls theme={theme} />}
        </div>
      </header>

      <main
        className="flex-1 w-full mx-auto flex flex-col px-4 pb-6 md:px-6"
        style={{ maxWidth }}
      >
        {children}
      </main>
    </div>
  );
}
